import React, { useEffect, useState } from 'react';
import axios from 'axios';

const cache = new Map();
const CACHE_MS = 18000;
const inflight = new Map();

function resolveSymbol(item) {
  if (!item) return '';
  const raw = item.symbol || item.stock || '';
  if (!raw) return '';
  if (/\.(NS|BO)$/i.test(raw) || raw.startsWith('^')) return raw.toUpperCase();
  const exch = String(item.exchange || '').toUpperCase();
  return `${String(raw).replace(/\.NS$/i, '').replace(/\.BO$/i, '').toUpperCase()}${exch === 'BSE' ? '.BO' : '.NS'}`;
}

/** Indian-style share qty: 1.2L · 45.3K · 980 */
export function fmtShareQty(n) {
  const v = Number(n) || 0;
  if (v >= 1e7) return `${(v / 1e7).toFixed(2)} Cr`;
  if (v >= 1e5) return `${(v / 1e5).toFixed(2)} L`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`;
  return String(Math.round(v));
}

function flowFromCandles(candles, dayVolume = 0) {
  let buyVolume = 0;
  let sellVolume = 0;
  for (const c of candles || []) {
    const vol = Number(c.volume) || 0;
    if (c.bull || Number(c.close) >= Number(c.open)) buyVolume += vol;
    else sellVolume += vol;
  }
  const totalVolume = buyVolume + sellVolume;
  const last = (candles || [])[(candles || []).length - 1];
  const buyPct = totalVolume ? Math.round((buyVolume / totalVolume) * 100) : 50;
  return {
    buyVolume,
    sellVolume,
    totalVolume,
    buyPct,
    sellPct: 100 - buyPct,
    lastBarVolume: Number(last?.volume) || 0,
    lastBarSide: last?.bull ? 'buy' : 'sell',
    dayVolume: Number(dayVolume) || totalVolume
  };
}

/** Classify short-term trend from 5m candles: uptrend / downtrend / sideways. */
export function detectCandleTrend(candles) {
  const rows = candles || [];
  if (rows.length < 3) {
    return { kind: 'sideways', label: 'Sideways', pct: 0 };
  }
  const closes = rows.map((c) => Number(c.close)).filter((n) => Number.isFinite(n) && n > 0);
  if (closes.length < 3) {
    return { kind: 'sideways', label: 'Sideways', pct: 0 };
  }
  const n = closes.length;
  const third = Math.max(1, Math.floor(n / 3));
  const avg = (arr) => arr.reduce((s, v) => s + v, 0) / arr.length;
  const early = avg(closes.slice(0, third));
  const late = avg(closes.slice(-third));
  const pct = early > 0 ? ((late - early) / early) * 100 : 0;

  // Recent momentum (last ~6 bars) to avoid stale early bias
  const recent = closes.slice(-Math.min(6, n));
  const recentPct = recent[0] > 0
    ? ((recent[recent.length - 1] - recent[0]) / recent[0]) * 100
    : 0;
  const blended = (pct * 0.55) + (recentPct * 0.45);
  const roundPct = Math.round(blended * 100) / 100;

  // ~0.2% band = sideways for intraday 5m (penny + mid-price friendly)
  if (blended >= 0.2) return { kind: 'up', label: 'Uptrend', pct: roundPct };
  if (blended <= -0.2) return { kind: 'down', label: 'Downtrend', pct: roundPct };
  return { kind: 'sideways', label: 'Sideways', pct: roundPct };
}

async function loadCandlePack(symbol) {
  const key = symbol.toUpperCase();
  const hit = cache.get(key);
  if (hit && Date.now() - hit.at < CACHE_MS) return hit;
  if (inflight.has(key)) return inflight.get(key);
  const p = axios
    .get('/api/normal/candles', { params: { symbol: key }, timeout: 10000 })
    .then((res) => {
      const candles = res.data?.candles || [];
      const dayVolume = res.data?.dayVolume || 0;
      const flow = res.data?.flow || flowFromCandles(candles, dayVolume);
      const pack = { at: Date.now(), candles, dayVolume, flow };
      cache.set(key, pack);
      return pack;
    })
    .catch(() => {
      const prev = cache.get(key);
      if (prev) return prev;
      return { at: Date.now(), candles: [], dayVolume: 0, flow: flowFromCandles([]) };
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, p);
  return p;
}

function LivePeopleFlow({ flow, liveDayVolume }) {
  if (!flow) return null;
  const dayVol = Number(liveDayVolume) > 0 ? Number(liveDayVolume) : (flow.dayVolume || flow.totalVolume || 0);
  const buyPct = Math.max(0, Math.min(100, flow.buyPct ?? 50));
  const sellPct = Math.max(0, Math.min(100, flow.sellPct ?? (100 - buyPct)));
  const lastSide = flow.lastBarSide === 'sell' ? 'sell' : 'buy';

  return (
    <div className="tab-live-flow" aria-label="Live market buy and sell quantity">
      <div className="tab-live-flow-head">
        <span className="tab-live-flow-badge">LIVE</span>
        <strong>People buying / selling</strong>
        <em>Yahoo qty · today</em>
      </div>
      <div className="tab-live-flow-bars" role="img" aria-label={`Buying ${buyPct}% selling ${sellPct}%`}>
        <div className="tab-live-flow-buy" style={{ width: `${buyPct}%` }} />
        <div className="tab-live-flow-sell" style={{ width: `${sellPct}%` }} />
      </div>
      <div className="tab-live-flow-rows">
        <div className="tab-live-flow-row buy">
          <span>🟢 Buying</span>
          <strong>{fmtShareQty(flow.buyVolume)} sh</strong>
          <em>{buyPct}%</em>
        </div>
        <div className="tab-live-flow-row sell">
          <span>🔴 Selling</span>
          <strong>{fmtShareQty(flow.sellVolume)} sh</strong>
          <em>{sellPct}%</em>
        </div>
      </div>
      <div className="tab-live-flow-meta">
        <span>Today vol <strong>{fmtShareQty(dayVol)}</strong></span>
        <span className={lastSide}>
          Last 5m <strong>{fmtShareQty(flow.lastBarVolume)}</strong>
          {lastSide === 'buy' ? ' buy' : ' sell'}
        </span>
      </div>
    </div>
  );
}

/**
 * Compact Yahoo 5m candlestick + live buy/sell people qty for V1 cards.
 */
export default function MiniCandleChart({
  item,
  entryPrice = null,
  exitPrice = null,
  height = 72
}) {
  const symbol = resolveSymbol(item);
  const cached = cache.get(symbol.toUpperCase());
  const [candles, setCandles] = useState(() => cached?.candles || []);
  const [flow, setFlow] = useState(() => cached?.flow || null);
  const [dayVolume, setDayVolume] = useState(() => cached?.dayVolume || 0);
  const [loading, setLoading] = useState(!candles.length);

  useEffect(() => {
    if (!symbol) return undefined;
    let cancelled = false;
    setLoading(!cache.get(symbol.toUpperCase())?.candles?.length);

    const apply = (pack) => {
      if (cancelled || !pack) return;
      setCandles(pack.candles || []);
      setFlow(pack.flow || flowFromCandles(pack.candles || [], pack.dayVolume));
      setDayVolume(pack.dayVolume || 0);
      setLoading(false);
    };

    loadCandlePack(symbol).then(apply);
    const timer = setInterval(() => {
      loadCandlePack(symbol).then(apply);
    }, 15000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [symbol]);

  const w = 220;
  const h = height;
  const rows = candles || [];
  const liveDayVolume = item?.volume > 0 ? item.volume : dayVolume;

  if (!symbol) return null;

  if (!rows.length) {
    return (
      <div className="tab-mini-candle" aria-hidden={!loading}>
        <div className="tab-mini-candle-head">
          <span>5m chart</span>
          <em>{loading ? 'Loading…' : 'No data'}</em>
        </div>
        <div className="tab-mini-candle-empty" style={{ height: h }} />
        {flow && <LivePeopleFlow flow={flow} liveDayVolume={liveDayVolume} />}
      </div>
    );
  }

  const lows = rows.map((c) => c.low);
  const highs = rows.map((c) => c.high);
  let min = Math.min(...lows);
  let max = Math.max(...highs);
  const markers = [entryPrice, exitPrice].filter((n) => n != null && Number(n) > 0).map(Number);
  for (const m of markers) {
    min = Math.min(min, m);
    max = Math.max(max, m);
  }
  const range = max - min || 1;
  const barW = w / rows.length;
  const y = (v) => h - ((v - min) / range) * (h - 10) - 5;
  const last = rows[rows.length - 1];
  const displayFlow = flow || flowFromCandles(rows, liveDayVolume);
  const trend = detectCandleTrend(rows);
  const trendIcon = trend.kind === 'up' ? '↑' : trend.kind === 'down' ? '↓' : '↔';

  return (
    <div className="tab-mini-candle" title={`${symbol} · 5m candles · live volume · ${trend.label}`}>
      <div className="tab-mini-candle-head">
        <span>5m · {rows.length} bars</span>
        <span className={`tab-trend-badge ${trend.kind}`} title={`${trend.label} · ${trend.pct >= 0 ? '+' : ''}${trend.pct}% vs early session`}>
          {trendIcon} {trend.label}
        </span>
        <strong className={last?.bull ? 'up' : 'down'}>
          ₹{Number(last.close).toFixed(2)}
        </strong>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="tab-mini-candle-svg" preserveAspectRatio="none" width="100%" height={h}>
        {entryPrice != null && Number(entryPrice) > 0 && (
          <line
            x1="0"
            x2={w}
            y1={y(Number(entryPrice))}
            y2={y(Number(entryPrice))}
            stroke="#2563eb"
            strokeWidth="1"
            strokeDasharray="3 2"
            opacity="0.85"
          />
        )}
        {exitPrice != null && Number(exitPrice) > 0 && (
          <line
            x1="0"
            x2={w}
            y1={y(Number(exitPrice))}
            y2={y(Number(exitPrice))}
            stroke="#b45309"
            strokeWidth="1"
            strokeDasharray="3 2"
            opacity="0.85"
          />
        )}
        {rows.map((c, i) => {
          const x = i * barW + barW * 0.2;
          const bw = Math.max(1.2, barW * 0.55);
          const color = c.bull ? '#1a7a4a' : '#b83232';
          const bodyTop = Math.min(y(c.open), y(c.close));
          const bodyH = Math.max(1.5, Math.abs(y(c.open) - y(c.close)));
          return (
            <g key={`mc-${symbol}-${c.t || i}`}>
              <line x1={x + bw / 2} y1={y(c.high)} x2={x + bw / 2} y2={y(c.low)} stroke={color} strokeWidth="1" />
              <rect x={x} y={bodyTop} width={bw} height={bodyH} fill={color} />
            </g>
          );
        })}
      </svg>
      {(entryPrice != null || exitPrice != null) && (
        <div className="tab-mini-candle-legend">
          {entryPrice != null && <span className="entry">Buy ₹{Number(entryPrice).toFixed(2)}</span>}
          {exitPrice != null && <span className="exit">Sell ₹{Number(exitPrice).toFixed(2)}</span>}
        </div>
      )}
      <LivePeopleFlow flow={displayFlow} liveDayVolume={liveDayVolume} />
    </div>
  );
}
